import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRinging, Check, Crown, Eye, EyeSlash, HeartStraight, PencilSimple, RocketLaunch, Scan, SignOut, Storefront, UserPlus, UsersThree, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../../lib/api";
import { useAuth, homeFor } from "../../context/AuthContext";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import { disablePush, enablePush, getPushStatus } from "../../lib/push";
import { PROMOTER_TITLES } from "../../lib/promoterTitles";
import MediaUpload from "../../components/MediaUpload";
import { formatPlanPrice, intervalLabel, yearlySavings, BillingToggle, usePricing } from "../../hooks/usePricing";
import { resolveCheckoutCity } from "../../hooks/usePublicCity";
import FounderCityDialog from "../../components/FounderCityDialog";

const VISIBILITY = [
  { id: "public", label: "Public" },
  { id: "friends", label: "Friends Only" },
  { id: "close_friends", label: "Close Friends" },
  { id: "hidden", label: "Hidden" },
];

export async function startCheckout(lookupKey, opts = {}) {
  const payload = { lookup_key: lookupKey, origin_url: window.location.origin };
  if (lookupKey === "venue_yearly") {
    const city = opts.city || await resolveCheckoutCity();
    if (city) payload.city = city;
  }
  try {
    const { data } = await API.post("/payments/checkout", payload);
    if (data.founder_sold_out) {
      toast.info(`Founder spots in ${payload.city} are gone — the regular yearly rate applies.`);
    }
    window.location.href = data.checkout_url;
  } catch (e) {
    const detail = e?.response?.data?.detail || "";
    if (lookupKey === "venue_yearly" && detail.startsWith("CITY_REQUIRED") && opts.onCityRequired) {
      const manual = await opts.onCityRequired();
      if (manual) return startCheckout(lookupKey, { ...opts, city: manual });
    }
    throw e;
  }
}

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const { canInstall, installed, isIOS, install } = useInstallPrompt();
  const pricing = usePricing();
  const [favs, setFavs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [busy, setBusy] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [friends, setFriends] = useState(null);
  const [friendQuery, setFriendQuery] = useState("");
  const [bioValue, setBioValue] = useState("");
  const [pushState, setPushState] = useState({ supported: false, enabled: false, subscriptions: 0 });
  const [pushBusy, setPushBusy] = useState(false);
  const [promoterBilling, setPromoterBilling] = useState("yearly");
  const [venueBilling, setVenueBilling] = useState("yearly");
  const [cityAsk, setCityAsk] = useState(null);
  const navigate = useNavigate();

  const askCity = () => new Promise((resolve) => setCityAsk(() => resolve));

  const loadFriends = () => API.get("/friends/").then((r) => setFriends(r.data)).catch(() => {});

  useEffect(() => {
    API.get("/favorites").then((r) => setFavs(r.data)).catch(() => {});
    API.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    API.post("/notifications/read-all").catch(() => {});
    getPushStatus().then(setPushState).catch(() => {});
    loadFriends();
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState.enabled) await disablePush();
      else await enablePush();
      const status = await getPushStatus();
      setPushState(status);
      toast.success(status.enabled ? "Push notifications enabled" : "Push notifications disabled");
    } catch (e) {
      toast.error(e?.message || fmtErr(e));
    } finally {
      setPushBusy(false);
    }
  };

  const saveName = async () => {
    if (!nameValue.trim()) return setEditingName(false);
    try {
      await API.patch("/me", { name: nameValue.trim() });
      await refreshUser();
      setEditingName(false);
      toast.success("Name updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const savePromoterTitle = async (promoterTitle) => {
    try {
      await API.patch("/me", { promoter_title: promoterTitle });
      await refreshUser();
      toast.success(`Promoter title set to ${promoterTitle}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => { setBioValue(user?.bio || ""); }, [user?.bio]);

  const saveAvatar = async (media) => {
    try {
      await API.patch("/me", { avatar_url: media.url });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveBio = async () => {
    try {
      await API.patch("/me", { bio: bioValue });
      await refreshUser();
      toast.success("Promoter profile updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const RECAP_PREF_KEYS = [["guests", "Guests"], ["paid", "Paid entries"], ["revenue", "Revenue"], ["venues", "Venue names"]];
  const recapPrefs = { guests: true, paid: true, revenue: true, venues: true, ...(user?.recap_prefs || {}) };

  const toggleRecapPref = async (key) => {
    try {
      await API.patch("/me", { recap_prefs: { [key]: !recapPrefs[key] } });
      await refreshUser();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sendFriendRequest = async () => {
    try {
      const { data } = await API.post("/friends/request", { query: friendQuery });
      toast.success(`Request sent to ${data.to.handle}`);
      setFriendQuery("");
      loadFriends();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const respondFriend = async (requestId, accept) => {
    try {
      await API.post(`/friends/${requestId}/respond`, { accept });
      toast.success(accept ? "Friend approved" : "Request declined");
      loadFriends();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const unfriend = async (id) => {
    try {
      await API.delete(`/friends/${id}`);
      toast.success("Removed from friends");
      loadFriends();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setVisibility = async (v) => {
    try {
      await API.patch("/me", { visibility: v });
      await refreshUser();
      toast.success(`Visibility set to ${VISIBILITY.find((x) => x.id === v)?.label}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const checkout = async (key) => {
    setBusy(key);
    try {
      await startCheckout(key, { onCityRequired: askCity });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="profile-page" className="px-5 pt-8">
      <div className="flex items-center gap-4">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-neon-gold bg-neon-gold/15 shadow-glow-gold">
          {user?.avatar_url ? (
            <img src={mediaUrl(user.avatar_url)} alt={user.name} className="h-full w-full object-cover" data-testid="profile-avatar" />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-display text-2xl font-black text-neon-gold" data-testid="profile-avatar-fallback">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input data-testid="name-edit-input" autoFocus value={nameValue} maxLength={40}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveName()}
                className="w-full rounded-lg border border-neon-gold/50 bg-black px-3 py-1.5 font-display text-lg font-black outline-none" />
              <button data-testid="name-save-btn" onClick={saveName} className="rounded-full bg-neon-gold p-1.5 text-black"><Check size={15} weight="bold" /></button>
              <button onClick={() => setEditingName(false)} className="rounded-full border border-white/15 p-1.5 text-zinc-400"><X size={15} /></button>
            </div>
          ) : (
            <button data-testid="edit-name-btn" className="group flex items-center gap-2"
              onClick={() => { setNameValue(user?.name || ""); setEditingName(true); }}>
              <span className="font-display text-xl font-black" data-testid="profile-name">{user?.name}</span>
              <PencilSimple size={14} className="text-zinc-600 transition-colors group-hover:text-neon-gold" />
            </button>
          )}
          <div className="text-xs font-bold text-neon-violet">{user?.handle}</div>
          {user?.role === "promoter" && (
            <select data-testid="promoter-title-select" value={user?.promoter?.title || "Party Promoter"}
              onChange={(e) => savePromoterTitle(e.target.value)}
              className="mt-2 rounded-full border border-neon-gold/40 bg-neon-gold/10 px-3 py-1.5 text-[11px] font-black text-neon-gold outline-none">
              {PROMOTER_TITLES.map((title) => <option key={title} value={title}>{title}</option>)}
            </select>
          )}
          <div className="mt-0.5 text-[11px] uppercase tracking-widest text-zinc-500">{user?.role} · {user?.nite_points || 0} pts</div>
        </div>
      </div>

      <div className="mt-4" data-testid="avatar-upload-panel">
        <MediaUpload imageOnly clearAfterUpload label="Change profile photo" onUploaded={saveAvatar} />
      </div>

      {user?.role === "promoter" && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="promoter-public-profile-editor">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Public promoter description</div>
          <textarea data-testid="promoter-bio-input" value={bioValue} maxLength={500} rows={3}
            onChange={(e) => setBioValue(e.target.value)}
            placeholder="Tell venues and guests what kind of nights you create..."
            className="mt-2 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none focus:border-neon-gold" />
          <div className="mt-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Show on recap card</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="recap-prefs">
              {RECAP_PREF_KEYS.map(([key, label]) => (
                <button key={key} data-testid={`recap-pref-${key}`} onClick={() => toggleRecapPref(key)}
                  className={`rounded-full border px-3 py-1.5 text-[10px] font-bold transition-colors duration-200 ${recapPrefs[key] ? "border-neon-gold/50 bg-neon-gold/10 text-neon-gold" : "border-white/10 text-zinc-600"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-zinc-600">{bioValue.length}/500</span>
            <div className="flex items-center gap-2">
              <button data-testid="promoter-public-link" onClick={() => navigate(`/${user?.handle}`)}
                className="rounded-full border border-neon-violet/40 px-4 py-1.5 text-[11px] font-bold text-neon-violet">View Public Page</button>
              <a data-testid="promoter-share-card-btn"
                href={mediaUrl(`/api/promoters/handle/${encodeURIComponent(user?.handle || "")}/card.png`)}
                className="rounded-full border border-neon-gold/40 px-4 py-1.5 text-[11px] font-bold text-neon-gold">Share Card</a>
              <a data-testid="promoter-recap-card-btn"
                href={mediaUrl(`/api/promoters/handle/${encodeURIComponent(user?.handle || "")}/recap.png`)}
                className="rounded-full border border-neon-violet/40 px-4 py-1.5 text-[11px] font-bold text-neon-violet">Recap Card</a>
              <a data-testid="promoter-guestlist-card-btn"
                href={mediaUrl(`/api/promoters/handle/${encodeURIComponent(user?.handle || "")}/guestlist.png`)}
                className="rounded-full bg-neon-violet px-4 py-1.5 text-[11px] font-black text-black">Guest List Card</a>
              <button data-testid="promoter-bio-save-btn" onClick={saveBio}
                className="rounded-full bg-neon-gold px-4 py-1.5 text-[11px] font-black text-black">Save Profile</button>
            </div>
          </div>
        </div>
      )}

      {(user?.role === "promoter" || user?.role === "venue") && (
        <button data-testid="open-dashboard-btn" onClick={() => navigate(homeFor(user))}
          className="btn-ghost-neon mt-5 w-full !border-neon-violet/40">
          <RocketLaunch size={18} /> Open {user.role === "venue" ? "Venue" : "Promoter"} Dashboard
        </button>
      )}

      {(user?.staff_of || []).length > 0 && (
        <button data-testid="staff-scanner-btn" onClick={() => navigate("/staff-scanner")}
          className="btn-ghost-neon mt-3 w-full !border-neon-green/40 !text-neon-green">
          <Scan size={18} /> Open Door Scanner
        </button>
      )}

      <section className="mt-7">
        <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-bold"><Eye size={15} className="text-neon-violet" /> WHO SEES YOUR PLANS</h2>
        <div className="grid grid-cols-4 gap-1.5" data-testid="visibility-select">
          {VISIBILITY.map((v) => (
            <button key={v.id} data-testid={`visibility-${v.id}`} onClick={() => setVisibility(v.id)}
              className={`rounded-xl border px-1 py-2.5 text-[10px] font-bold transition-[border-color,background-color] duration-200 ${user?.visibility === v.id ? "border-neon-gold bg-neon-gold/15 text-white" : "border-white/10 text-zinc-500 hover:border-white/25"}`}>
              {v.id === "hidden" ? <EyeSlash size={12} className="mx-auto mb-1" /> : <Eye size={12} className="mx-auto mb-1" />}
              {v.label}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-7">
        <h2 className="mb-1 flex items-center gap-2 font-display text-sm font-bold"><UsersThree size={15} className="text-neon-violet" /> FRIENDS</h2>
        <p className="mb-2 text-[11px] text-zinc-500">Only friends you approve can see your plans for the night.</p>
        <div className="flex gap-2">
          <input data-testid="friend-query-input" value={friendQuery} onChange={(e) => setFriendQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendFriendRequest()}
            placeholder="Friend's email or @handle"
            className="flex-1 rounded-xl border border-white/10 bg-black/50 px-4 py-2.5 text-sm outline-none transition-[border-color] focus:border-neon-violet" />
          <button data-testid="friend-add-btn" onClick={sendFriendRequest}
            className="rounded-full bg-neon-violet px-4 py-2 text-xs font-black text-black shadow-glow-violet">
            <UserPlus size={15} weight="bold" />
          </button>
        </div>
        {friends?.incoming?.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="incoming-requests">
            {friends.incoming.map((f) => (
              <div key={f.request_id} className="flex items-center justify-between rounded-xl border border-neon-violet/30 bg-neon-violet/5 px-4 py-2.5">
                <span className="flex items-center gap-2 text-sm">
                  {f.avatar_url ? <img src={mediaUrl(f.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-neon-violet/15 text-xs font-black text-neon-violet">{f.name?.[0]}</span>}
                  <span><span className="font-bold">{f.name}</span> <span className="text-xs text-neon-violet">{f.handle}</span></span>
                </span>
                <div className="flex gap-2">
                  <button data-testid={`friend-accept-${f.request_id}`} onClick={() => respondFriend(f.request_id, true)}
                    className="rounded-full bg-neon-green px-3 py-1 text-[11px] font-black text-black">Approve</button>
                  <button data-testid={`friend-decline-${f.request_id}`} onClick={() => respondFriend(f.request_id, false)}
                    className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-bold text-zinc-400">Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {friends?.outgoing?.length > 0 && (
          <p className="mt-2 text-[11px] text-zinc-500">Pending: {friends.outgoing.map((f) => f.handle).join(", ")}</p>
        )}
        <div className="mt-3 space-y-2" data-testid="friends-list">
          {friends?.friends?.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
              <span className="flex items-center gap-2 text-sm">
                {f.avatar_url ? <img src={mediaUrl(f.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs font-black text-zinc-400">{f.name?.[0]}</span>}
                <span><span className="font-bold">{f.name}</span> <span className="text-xs text-neon-violet">{f.handle}</span></span>
              </span>
              <button data-testid={`friend-remove-${f.id}`} onClick={() => unfriend(f.id)}
                className="text-[11px] font-bold text-zinc-500 transition-colors hover:text-red-400">Remove</button>
            </div>
          ))}
          {friends && friends.friends.length === 0 && friends.incoming.length === 0 && (
            <p className="py-2 text-sm text-zinc-500">No friends yet — add your crew to share plans.</p>
          )}
        </div>
      </section>

      <section className="mt-7">
        <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-bold"><BellRinging size={15} className="text-neon-yellow" /> NOTIFICATIONS</h2>
        <div className="mb-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="push-settings">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-black">Web Push Alerts</div>
              <div className="mt-0.5 text-[11px] text-zinc-500">
                {pushState.supported
                  ? pushState.enabled ? `On for ${pushState.subscriptions} device${pushState.subscriptions === 1 ? "" : "s"}` : "Get referrals, friends, rewards, and drops when NITEPASS is closed."
                  : "This browser does not support Web Push."}
              </div>
            </div>
            <button data-testid="push-toggle-btn" disabled={!pushState.supported || pushBusy} onClick={togglePush}
              className={`rounded-full px-4 py-2 text-xs font-black ${pushState.enabled ? "border border-neon-green/40 text-neon-green" : "bg-neon-gold text-black shadow-glow-gold"} disabled:opacity-40`}>
              {pushBusy ? "..." : pushState.enabled ? "Enabled" : "Enable"}
            </button>
          </div>
        </div>
        <div className="space-y-2" data-testid="notifications-list">
          {notifications.slice(0, 5).map((n) => (
            <div key={n.id} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <div className="text-xs font-black text-neon-gold">{n.title}</div>
              <div className="mt-0.5 text-xs text-zinc-400">{n.body}</div>
            </div>
          ))}
          {notifications.length === 0 && <p className="py-3 text-sm text-zinc-500">All quiet. Favorite venues and promoters to get their drops.</p>}
        </div>
      </section>

      <section className="mt-7">
        <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-bold"><HeartStraight size={15} className="text-neon-gold" /> FAVORITES</h2>
        <div className="flex flex-wrap gap-2" data-testid="favorites-list">
          {favs.map((f, i) => (
            <button key={i} data-testid={`favorite-${f.type}-${i}`}
              onClick={() => f.type === "venue" ? navigate(`/venue/${f.item.id}`) : f.type === "promoter" && navigate(`/${f.item.handle}`)}
              className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-bold transition-colors duration-200 hover:border-neon-gold/50">
              {f.type === "promoter" ? f.item.handle : f.item.name || f.item.title}
            </button>
          ))}
          {favs.length === 0 && <p className="text-sm text-zinc-500">Tap the heart on any venue to build your night list.</p>}
        </div>
      </section>

      {user?.role === "guest" && (
        <section className="mt-7">
          <h2 className="mb-2 font-display text-sm font-bold">RUN THE NIGHT</h2>
          <div className="space-y-3">
            <div className="card-neon p-4" data-testid="upgrade-promoter">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RocketLaunch size={22} className="text-neon-violet" />
                  <div>
                    <div className="text-sm font-black">NITEPASS PROMOTER</div>
                    <div className="text-[11px] text-zinc-500" data-testid="promoter-price-line">
                      {formatPlanPrice(pricing[`promoter_${promoterBilling}`])}/{intervalLabel(pricing[`promoter_${promoterBilling}`])} · guest lists + earnings
                    </div>
                  </div>
                </div>
                <BillingToggle value={promoterBilling} onChange={setPromoterBilling} testid="promoter-billing-toggle"
                  savings={yearlySavings(pricing.promoter_monthly, pricing.promoter_yearly)} />
              </div>
              <button data-testid="checkout-promoter-btn" disabled={busy} onClick={() => checkout(`promoter_${promoterBilling}`)}
                className="mt-3 w-full rounded-full bg-neon-violet px-4 py-2.5 text-xs font-black text-black disabled:opacity-50">
                {busy ? "..." : `Go Pro — ${formatPlanPrice(pricing[`promoter_${promoterBilling}`])}/${promoterBilling === "monthly" ? "mo" : "yr"}`}
              </button>
            </div>
            <div className="card-neon border-neon-gold/40 p-4" data-testid="upgrade-venue">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Storefront size={22} className="text-neon-gold" />
                  <div>
                    <div className="text-sm font-black">NITEPASS VENUE</div>
                    <div className="text-[11px] text-zinc-500" data-testid="venue-price-line">
                      {formatPlanPrice(pricing[`venue_${venueBilling}`])}/{intervalLabel(pricing[`venue_${venueBilling}`])} · scanner + analytics
                    </div>
                  </div>
                </div>
                <BillingToggle value={venueBilling} onChange={setVenueBilling} testid="venue-billing-toggle"
                  savings={yearlySavings(pricing.venue_monthly, pricing.venue_yearly)} />
              </div>
              <button data-testid="checkout-venue-btn" disabled={busy} onClick={() => checkout(`venue_${venueBilling}`)}
                className="mt-3 w-full rounded-full bg-neon-gold px-4 py-2.5 text-xs font-black text-black shadow-glow-gold disabled:opacity-50">
                {busy ? "..." : `List Venue — ${formatPlanPrice(pricing[`venue_${venueBilling}`])}/${venueBilling === "monthly" ? "mo" : "yr"}`}
              </button>
            </div>
          </div>
        </section>
      )}

      {user?.subscription?.status === "active" && user?.subscription?.plan !== "admin" && (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-neon-green/30 bg-neon-green/5 px-4 py-3 text-xs font-bold text-neon-green" data-testid="subscription-active">
          <Crown size={15} weight="fill" /> {user.subscription.plan?.toUpperCase()} membership active
        </div>
      )}

      <section className="mt-7" data-testid="get-the-app">
        <h2 className="mb-2 font-display text-sm font-bold">GET THE APP</h2>
        {installed ? (
          <div className="flex items-center gap-2 rounded-xl border border-neon-green/30 bg-neon-green/5 px-4 py-3 text-xs font-bold text-neon-green" data-testid="app-installed-chip">
            <Crown size={15} weight="fill" /> NITEPASS app installed — you're running the real thing
          </div>
        ) : canInstall ? (
          <button data-testid="install-app-btn" onClick={install} className="btn-neon w-full">
            Install the NITEPASS App
          </button>
        ) : isIOS ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs leading-relaxed text-zinc-400" data-testid="ios-install-instructions">
            On iPhone: tap <span className="font-bold text-white">Share</span> in Safari, then{" "}
            <span className="font-bold text-white">"Add to Home Screen"</span> — NITEPASS installs as a full-screen app, mirroring this site exactly.
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs leading-relaxed text-zinc-400">
            Open this page in Chrome or Safari on your phone to install NITEPASS as a home-screen app — it mirrors this site exactly.
          </div>
        )}
      </section>

      <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
        className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-3 text-sm font-bold text-zinc-400 transition-colors duration-200 hover:border-red-500/50 hover:text-red-400">
        <SignOut size={17} /> Sign out
      </button>

      <FounderCityDialog open={!!cityAsk}
        onSubmit={(c) => { cityAsk?.(c); setCityAsk(null); }}
        onCancel={() => { cityAsk?.(null); setCityAsk(null); }} />
    </div>
  );
}
